/**
 * Page chrome: the public masthead and footer, and the authenticated app shell.
 */

import { NavLink, Link, useLocation } from 'react-router-dom'
import { useRef } from 'react'
import type { MouseEvent, ReactNode } from 'react'

import { env } from '@/app/env'
import { useSession } from '@/app/providers'
import { useTheme, type ThemePreference } from '@/app/theme'
import { useAccount, useCatalog } from '@/shared/api/hooks'
import { capture } from '@/shared/lib/analytics'
import { usePageTitle } from '@/shared/lib/pageTitle'
import { Button, Label, Badge } from './primitives'

/**
 * The theme control: follow the system, force light, force dark.
 *
 * Icon-only, because the three glyphs are universal and a labelled control would be
 * the widest thing in a 60px masthead. Each segment still carries a full sentence as
 * its accessible name — "Match my system theme" says what will happen, where "System"
 * alone would only say what it is.
 */
export function ThemeSwitch() {
  const { preference, setPreference } = useTheme()

  const options: Array<{ value: ThemePreference; label: string; icon: ReactNode }> = [
    {
      value: 'system',
      label: 'Match my system theme',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="2.5" y="4" width="19" height="13" rx="1.5" />
          <path d="M8.5 20.5h7M12 17v3.5" />
        </svg>
      ),
    },
    {
      value: 'light',
      label: 'Use the light theme',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2v2.6M12 19.4V22M2 12h2.6M19.4 12H22M4.9 4.9l1.9 1.9M17.2 17.2l1.9 1.9M19.1 4.9l-1.9 1.9M6.8 17.2l-1.9 1.9" />
        </svg>
      ),
    },
    {
      value: 'dark',
      label: 'Use the dark theme',
      icon: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 14.2A8.2 8.2 0 1 1 9.8 4a6.6 6.6 0 0 0 10.2 10.2Z" />
        </svg>
      ),
    },
  ]

  return (
    <div className="themeswitch" role="group" aria-label="Colour theme">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="themeswitch__btn"
          aria-pressed={preference === option.value}
          aria-label={option.label}
          title={option.label}
          onClick={() => setPreference(option.value)}
        >
          {option.icon}
        </button>
      ))}
    </div>
  )
}

/**
 * The lockup: mark, then wordmark.
 *
 * There is no version beside the name. It used to read "v1", which said nothing true —
 * the CLI was on 0.35 while the portal claimed a major it had never had, and a version
 * in a masthead is a number nobody maintains (#54).
 *
 * The icon is the "A" on its own rather than the full logo. The full logo is a monogram
 * that already reads as letterforms, so setting it beside the word "Archis" would say the
 * name twice in two different voices. The "A" is a mark; "Archis" is the name.
 *
 * Dimensions are on the element, not only in CSS, so the masthead does not reflow when
 * the image decodes — an unsized image next to text is a visible jump on first paint.
 * `alt` is empty on purpose: the link already carries the accessible name, and a second
 * copy would make a screen reader announce "Archis logo Archis home".
 */
export function Wordmark({ to = '/' }: { to?: string }) {
  return (
    <Link to={to} className="wordmark" aria-label="Archis home">
      <img
        className="wordmark__mark"
        src="/brand/chi-mark.svg"
        alt=""
        width={32}
        height={32}
        decoding="async"
      />
      <span className="wordmark__text" aria-hidden="true">
        rchis
      </span>
      <span className="sr-only">Archis</span>
    </Link>
  )
}

/**
 * The documentation link.
 *
 * `/docs` is part of this app now, so the default is a client-side route. The
 * `VITE_DOCS_URL` escape hatch stays for the case where documentation is ever
 * served from its own host — it takes precedence and renders a plain anchor, since a
 * router link to another origin does nothing.
 */
function DocsLink({ className, children }: { className?: string; children: ReactNode }) {
  if (env.docsUrl) {
    return (
      <a className={className} href={env.docsUrl}>
        {children}
      </a>
    )
  }
  return (
    <Link className={className} to="/docs">
      {children}
    </Link>
  )
}

/** The primary links, shared by the desktop nav row and the phone menu. */
function PrimaryLinks() {
  return (
    <>
      {/* A real page rather than an anchor: the walkthrough is where somebody
          deciding whether to trust the numbers goes, and it deserves a URL they
          can send to the colleague who has to approve the spend. */}
      <Link className="masthead__link" to="/how-it-works">
        How it works
      </Link>
      <DocsLink className="masthead__link">Docs</DocsLink>
      <Link className="masthead__link" to="/pricing">
        Pricing
      </Link>
      <Link className="masthead__link" to="/enterprise">
        Enterprise
      </Link>
      <Link className="masthead__link" to="/verify">
        Check it yourself
      </Link>
      <Link className="masthead__link" to="/changelog">
        Changelog
      </Link>
    </>
  )
}

/** Public masthead, used on the landing, pricing and legal pages. */
export function Masthead() {
  const { user, ready } = useSession()
  // The primary CTA follows what the backend will actually accept. Signing up is
  // now the front door on every deployment that has not deliberately closed it —
  // an account with nothing behind it gets Archis Free — so the CTA offers that, and
  // only steps back to the pricing page where the API would refuse to create one.
  const { data: catalog } = useCatalog()
  const inviteOnly = catalog?.invite_only_signups ?? false
  // The button says what it does. "Start free" was a price said as reassurance.
  const { pathname } = useLocation()
  // On the home page Install scrolls to the command box; everywhere else it goes to the
  // install guide, where every page CTA and the footer already point (#946). From the
  // docs, a trip to the home page was a way out of the docs.
  const ctaTo = pathname === '/' ? '/#install' : '/docs/install'
  const ctaLabel = 'Install'
  const menu = useRef<HTMLDetailsElement>(null)
  // A tap on a link inside the menu navigates; the menu should not stay open over the
  // page it just opened.
  const closeMenu = (event: MouseEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('a') && menu.current) menu.current.open = false
  }

  return (
    <>
      <a className="skip" href="#main">Skip to content</a>
    <header className="masthead">
      <div className="shell masthead__inner">
        <Wordmark />
        {/*
          Anchors into the landing, plus the pages that earn a nav slot of their own.
          Docs is one of them: for a developer tool it is the second thing an evaluator
          opens, and it used to be missing because the link pointed at a hostname with
          no DNS record. It now points at /docs in this app.

          The anchors are absolute (`/#how`) so they also work from /pricing and the
          legal pages, where a bare `#how` would just scroll to nothing.
        */}
        <nav className="masthead__nav" aria-label="Primary">
          <PrimaryLinks />
        </nav>
        <div className="row" style={{ flexWrap: 'nowrap', gap: 'var(--s2)' }}>
          <details className="masthead__menu" ref={menu}>
            <summary>Menu</summary>
            <nav className="masthead__menupanel" aria-label="Primary" onClick={closeMenu}>
              <PrimaryLinks />
            </nav>
          </details>
          <ThemeSwitch />
          {ready && user ? (
            // A Link, not a window.location assignment: this is a client-side route and
            // reloading the whole bundle to reach it is a second of nothing happening.
            <Link to="/account" className="cta cta--primary" style={{ padding: '0.5rem 1.1rem', fontSize: '0.875rem' }}>
              Open the app
            </Link>
          ) : (
            <>
              {/*
                No "Sign in" while Archis is invitation-only. There is nothing to sign
                in to unless we have already emailed you a link, and an account
                cannot be created from here at all — the API refuses an address it
                never invited, so offering the door would be advertising a room that
                is locked. The one thing a visitor can do is take a place, and that
                is the button next to this comment.

                The page still exists at /signin for the people arriving from an
                invitation, from `chi auth login`, or with a lapsed session; it is simply
                not advertised. Once signups open, this brings the link back.
              */}
              {!inviteOnly && (
                <Link className="masthead__link" to="/signin">
                  Sign in
                </Link>
              )}
              <Link
                to={ctaTo}
                className="cta cta--primary"
                style={{ padding: '0.5rem 1.1rem', fontSize: '0.875rem' }}
                onClick={() => capture('header_install_clicked', { page: pathname })}
              >
                {ctaLabel}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
    </>
  )
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="shell stack">
        <div className="footer__cols">
          <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
            <Wordmark />
            {/* The site's one plain-English sentence about what Archis is. */}
            <p style={{ margin: 0, maxWidth: '28ch' }}>
              An upgrade for the coding agent you already use: an index of your repository,
              built and served on your machine.
            </p>
            {/* Also here because a 320px masthead has no room for it. */}
            <ThemeSwitch />
          </div>
          <div>
            <div className="footer__heading">Product</div>
            <ul className="footer__list">
              {/* The real page, not the landing anchor. The anchor still exists, but a
                  reader clicking "How it works" in a footer wants the walkthrough. */}
              <li>
                <Link to="/how-it-works">How it works</Link>
              </li>
              <li>
                <Link to="/enterprise">Enterprise</Link>
              </li>
              <li>
                <Link to="/whitepaper">Whitepaper</Link>
              </li>
              <li>
                <Link to="/examples">Examples</Link>
              </li>
              <li>
                <Link to="/pricing">Pricing</Link>
              </li>
              <li>
                <Link to="/docs/install">Install</Link>
              </li>
              <li>
                <Link to="/verify">Check it yourself</Link>
              </li>
              <li>
                <Link to="/changelog">Changelog</Link>
              </li>
              <li>
                <DocsLink>Documentation</DocsLink>
              </li>
              <li>
                <Link to="/docs/cli">CLI reference</Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="footer__heading">Legal</div>
            <ul className="footer__list">
              <li>
                <Link to="/terms">Terms &amp; EULA</Link>
              </li>
              <li>
                <Link to="/privacy">Privacy</Link>
              </li>
              <li>
                <Link to="/privacy#telemetry">What we collect</Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="footer__heading">Support</div>
            <ul className="footer__list">
              <li>
                {/* The ticket queue, not the mailbox, is the channel of record: it
                    keeps the thread and the screenshots in one place. The address
                    stays below it for anyone who cannot sign in — which is exactly
                    the person least able to open a ticket. */}
                <Link to="/support">Open a ticket</Link>
              </li>
              <li>
                <a href={`mailto:${env.supportEmail}`}>{env.supportEmail}</a>
              </li>
              <li>
                <Link to="/account">Account portal</Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="ruler" style={{ margin: 'var(--s5) 0 0' }} />
        <div className="row row--between mono-note">
          <span>© {new Date().getFullYear()} Archis. All rights reserved.</span>
          <span>Closed source. Distributed as signed binaries.</span>
        </div>
      </div>
    </footer>
  )
}

/** Wraps a public page in the masthead and footer. */
export function PublicPage({ children }: { children: ReactNode }) {
  return (
    <>
      <Masthead />
      <main id="main">{children}</main>
      <Footer />
    </>
  )
}

interface RailItem {
  to: string
  label: string
  index: string
  badge?: ReactNode
}

/**
 * The authenticated shell: a left rail whose active item is marked by a phosphor
 * hairline rather than a filled pill, and a content column.
 *
 * Team sections only appear when the account actually belongs to a workspace —
 * showing a disabled "Seats" link to a Pro customer advertises a feature they did
 * not buy and cannot use.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useSession()
  const { data: account } = useAccount({ enabled: Boolean(user) })
  const location = useLocation()

  const organization =
    account?.organizations.find((org) => org.has_seat) ?? account?.organizations[0]
  const isEnterprise = account?.plan === 'enterprise'

  const accountItems: RailItem[] = [
    { to: '/account', label: 'Overview', index: '01' },
    { to: '/billing', label: 'Billing', index: '02' },
    { to: '/devices', label: 'Devices', index: '03' },
    { to: '/machine-tokens', label: 'Machine tokens', index: '04' },
    { to: '/downloads', label: 'Downloads', index: '05' },
    { to: '/support', label: 'Support', index: '06' },
    ...(!organization
      ? [
          {
            to: '/team',
            label: '+ Create Team',
            index: '07',
            badge: <Badge tone="signal">2 free</Badge>,
          },
        ]
      : []),
  ]

  const role = organization?.role ?? 'member'
  const isOwner = role === 'owner'
  const isAdmin = role === 'admin' || isOwner
  const isBillingAdmin = role === 'billing_admin' || isOwner
  const isManager = isAdmin || isBillingAdmin

  const isSuspended = organization?.status === 'suspended'

  const rawTeamItems: Omit<RailItem, 'index'>[] = (organization && !isSuspended)
    ? [
        { to: '/team', label: 'Workspace' },
        ...(isManager ? [{ to: '/team/seats', label: 'Seats' }] : []),
        ...(isAdmin ? [{ to: '/team/policies', label: 'Policies' }] : []),
        ...(isManager
          ? [
              {
                to: '/team/usage',
                label: 'Usage',
                badge: !isEnterprise ? <Badge>Enterprise</Badge> : undefined,
              },
            ]
          : []),
        ...(isAdmin
          ? [
              {
                to: '/team/enterprise',
                label: 'SSO & SCIM',
                badge: !isEnterprise ? <Badge>Enterprise</Badge> : undefined,
              },
              {
                to: '/team/offline-licences',
                label: 'Offline licences',
                badge: !isEnterprise ? <Badge>Enterprise</Badge> : undefined,
              },
            ]
          : []),
        // Every member: the downloads are for whoever installs the server; the
        // licence section on the page is for owners and admins.
        {
          to: '/team/cavsdb',
          label: 'CAVS DB Server',
          badge: !isEnterprise ? <Badge>Enterprise</Badge> : undefined,
        },
        ...(isManager ? [{ to: '/team/audit', label: 'Audit log' }] : []),
        // Every member: the views read a file on their own machine, and the
        // registry's own policy decides what any of them may read through it.
        {
          to: '/team/federation',
          label: 'Federation',
          badge: account?.plan !== 'team' && !isEnterprise ? <Badge>Team</Badge> : undefined,
        },
      ]
    : []

  const teamItems: RailItem[] = rawTeamItems.map((item, idx) => ({
    ...item,
    index: String(idx + 6).padStart(2, '0'),
  }))

  return (
    <div className="app">
      <aside className="rail">
        <Wordmark to="/account" />

        <nav className="rail__nav" aria-label="Account">
          <div className="rail__group">Account</div>
          {accountItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className="rail__link"
              end={item.to === '/account'}
            >
              <span>{item.label}</span>
              {item.badge}
            </NavLink>
          ))}

          {teamItems.length > 0 && (
            <>
              <div className="rail__group">{organization?.name ?? 'Team'}</div>
              {teamItems.map((item) => (
                <NavLink key={item.to} to={item.to} className="rail__link" end>
                  <span>{item.label}</span>
                  {item.badge}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div style={{ marginTop: 'auto', paddingTop: 'var(--s6)' }}>
          <div className="ruler" style={{ margin: '0 0 var(--s4)' }} />
          <div className="mono-note" style={{ overflowWrap: 'anywhere' }}>
            {account?.email ?? user?.email ?? ''}
          </div>
          <div className="row row--between" style={{ marginTop: 'var(--s2)' }}>
            <Button variant="ghost" size="sm" onClick={() => void signOut()} style={{ paddingLeft: 0 }}>
              Sign out
            </Button>
            <ThemeSwitch />
          </div>
        </div>
      </aside>

      <main className="main" id="main" key={location.pathname}>
        {children}
      </main>
    </div>
  )
}

/**
 * Standard page header inside the shell.
 *
 * `eyebrow` is the datasheet section name (§02 / BILLING) and `title` is the human
 * heading. They are different strings on purpose: the eyebrow classifies, the
 * heading speaks.
 */
export function PageHead({
  index,
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  index: string
  eyebrow: string
  title: string
  subtitle?: string
  actions?: ReactNode
}) {
  usePageTitle(title)
  return (
    <div className="page-head">
      <div>
        <Label index={index}>{eyebrow}</Label>
        {/* The page's one h1, at the size these pages have always used. */}
        <h1 className="page-head__title">{title}</h1>
        {subtitle && (
          <p className="dim" style={{ marginTop: 'var(--s3)', maxWidth: '58ch' }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  )
}
